/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReviewCard from "../components/ReviewCard";
import { api } from "../api";
import RequireRole from "../components/RequireRole";
import { useAuth } from "../auth/AuthProvider";

export default function ReviewSuppliers() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;

    if (!user) return;

    const fetchData = async () => {
      setLoading(true);
      setErr(null);
      try {
        const endpoint =
          user?.role === "lpo_submitter"
            ? "/secure/my-submissions"
            : "/api/suppliers/review-suppliers";

        const res = await api.get(endpoint);

        const raw = Array.isArray(res.data)
          ? res.data
          : res.data?.items ||
            res.data?.results ||
            res.data?.data ||
            [];

        // if (isMounted) {
        //   setSubmissions(raw);
        //   console.log("Fetched submissions:", raw);
        //   const lastHandledId = sessionStorage.getItem("lastHandledAction");

        //   if (user?.role === "lpo_submitter" && raw.length > 0) {
        //     const latest = raw[0];

        //     if (
        //     latest.intent === "action" &&
        //     latest.processing_status === "done" &&
        //     latest.id.toString() !== lastHandledId) 
        //     {
        //       const lastHandled = sessionStorage.getItem("lastHandledAction");

        //       if (String(latest.id) !== lastHandled) {
        //         sessionStorage.setItem("lastHandledAction", latest.id);
        //         console.log("🚀 REDIRECT SHOULD HAPPEN");
        //         navigate(`/confirm-action/${latest.id}`);
        //         return;
        //       }
        //       }
        //   }
        //   }
        if (isMounted) {
          console.log("Fetched submissions:", raw);

          if (user?.role === "lpo_submitter" && raw.length > 0) {
            const latest = raw[0];

            const lastHandledId = sessionStorage.getItem("lastHandledAction");

            if (
              latest?.intent?.toLowerCase?.() === "action" &&
              latest?.processing_status === "done" &&
              String(latest.id) !== lastHandledId
            ) {
              console.log("🚀 REDIRECTING");

              sessionStorage.setItem("lastHandledAction", String(latest.id));
              navigate(`/confirm-action/${latest.id}`);
              return;
            }
          }

          setSubmissions(raw);
        }
      } catch (e) {
        console.error("Error fetching submissions", e);
        if (isMounted) setErr(e?.message || "Failed to load submissions");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const pending = useMemo(() => {
    if (user?.role === "lpo_submitter") {
      return submissions || [];
    }
    const PENDING_STATES = new Set(["pending", "needs_review", null, undefined, ""]);
    return (submissions || []).filter((s) =>
      PENDING_STATES.has(String(s.approval_status || "").toLowerCase())
    );
  }, [submissions, user]);

  const count = pending.length;
  const isLpo = user?.role === "lpo_submitter";

  return (
    <RequireRole roles={["reviewer", "admin", "lpo_submitter"]}>
      <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen px-4 py-8 sm:py-10">
        <div className="w-full max-w-7xl mx-auto">

          {/* Page header */}
          <div className="flex flex-col items-center mb-10">
            <div className="bg-primary/10 p-3 rounded-lg mb-4">
              <span className="material-symbols-outlined text-primary text-4xl">
                {isLpo ? "inventory" : "rate_review"}
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              {isLpo ? "My Submissions" : "Supplier Review Dashboard"}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              {isLpo
                ? "View your submitted records"
                : "Review, edit, and approve supplier submissions"}
            </p>
          </div>

          {/* Stats bar */}
          <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 px-5 py-4 flex items-center gap-3 mb-8">
            <div className="bg-primary/10 p-2 rounded-lg">
              <span className="material-symbols-outlined text-primary text-base">pending_actions</span>
            </div>
            <div>
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                {isLpo ? "Total" : "Pending"}
              </span>
              <p className="text-2xl font-bold text-slate-900 dark:text-white leading-none mt-0.5">
                {count}
              </p>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="size-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error */}
          {err && !loading && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm mb-6">
              <span className="font-semibold">Couldn't load submissions — </span>
              {String(err)}
            </div>
          )}

          {/* Empty state */}
          {!loading && !err && count === 0 && (
            <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
              <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
                <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
                <div>
                  <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {isLpo
                      ? "You don't have any submissions yet."
                      : "No suppliers pending review right now."}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                    If you expect items here, check the API response shape and status values.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cards grid */}
          {!loading && !err && count > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {pending.map((s) => (
                <ReviewCard
                  key={s.id}
                  initialData={s}
                  onApproved={(id) =>
                    setSubmissions((prev) => prev.filter((item) => item.id !== id))
                  }
                />
              ))}
            </div>
          )}

          <div className="mt-10 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500">© 2025 Fluxonix Security. All rights reserved.</p>
          </div>
        </div>

        {/* Background glow */}
        <div className="fixed inset-x-0 bottom-0 -z-10 h-56 sm:h-64 pointer-events-none opacity-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
          <div className="absolute -bottom-28 -left-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
          <div className="absolute -bottom-28 -right-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
        </div>
      </div>
    </RequireRole>
  );
}
